import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, TrendingUp, Users, CheckCircle, Loader2 } from 'lucide-react';
import { useAuthStore } from '../hooks/useAuth';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

export default function DashboardPage() {
  const { user } = useAuthStore();

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['campaigns', user?.organization_id],
    queryFn: () => user?.organization_id ? api.getCampaigns(user.organization_id) : Promise.resolve([]),
    enabled: !!user?.organization_id,
  });

  const activeCampaigns = (campaigns || []).filter(c => c.status === 'active').length;
  const totalTargets = (campaigns || []).reduce((acc, c) => acc + (c.target_representatives || 0), 0);

  const stats = [
    { name: 'Active Campaigns', value: activeCampaigns.toString(), change: '+0', icon: TrendingUp },
    { name: 'Total Campaigns', value: (campaigns?.length || 0).toString(), change: '+0', icon: Users },
    { name: 'Target Responses', value: totalTargets.toString(), change: '', icon: CheckCircle },
  ];

  const recentCampaigns = (campaigns || []).slice(0, 5);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'paused': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back! Here's your overview.</p>
        </div>
        <Link to="/campaigns/new">
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </Link>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {stats.map((stat) => (
          <Card key={stat.name}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.name}</p>
                  <p className="text-3xl font-bold mt-1">{stat.value}</p>
                </div>
                <div className="p-3 bg-primary/10 rounded-full">
                  <stat.icon className="w-6 h-6 text-primary" />
                </div>
              </div>
              {stat.change && (
                <p className="text-sm text-green-600 mt-2">
                  {stat.change} from last week
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <div className="p-6 border-b border-border">
          <h2 className="text-lg font-semibold">Recent Campaigns</h2>
        </div>
        {recentCampaigns.length === 0 ? (
          <CardContent className="py-8 text-center text-muted-foreground">
            <p>No campaigns yet. Create your first campaign to get started.</p>
            <Link to="/campaigns/new">
              <Button variant="outline" className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </Link>
          </CardContent>
        ) : (
          <div className="divide-y divide-border">
            {recentCampaigns.map((campaign) => (
              <Link
                key={campaign.id}
                to={`/campaigns/${campaign.id}`}
                className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
              >
                <div>
                  <p className="font-medium">{campaign.name}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Badge variant="secondary" className={getStatusColor(campaign.status)}>
                      {campaign.status}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Target: {campaign.target_representatives || 0}
                    </span>
                  </div>
                </div>
                <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full"
                    style={{ width: '50%' }}
                  />
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
